import type {
  ApiKey,
  DataImportConfig,
  EmailSenderIdentity,
  Invoice,
  OrganizationAccessPolicy,
  OrganizationDefaults,
  OrganizationProfile,
  RetentionPolicy,
  SendingDomain,
  WebhookSubscription,
} from '@common/types';

import { ORGANIZATIONS } from './organizations.seed';

const PRIMARY_ORG_ID = ORGANIZATIONS[0].id;

export const ORGANIZATION_PROFILES_SEED: Readonly<Record<string, OrganizationProfile>> = {
  'sun-intl': {
    organizationId: 'sun-intl',
    legalName: 'Sun International Hotels Limited',
    tradingName: 'Sun International',
    logoUrl: '',
    registrationNumber: '1967/007528/06',
    taxNumber: 'ZA 4180107255',
    websiteUrl: 'https://www.suninternational.com',
    address: {
      line1: '27 Sandton Drive',
      line2: 'Sandhurst',
      city: 'Johannesburg',
      region: 'Gauteng',
      postalCode: '2196',
      country: 'South Africa',
    },
    primaryContact: {
      name: 'Sarah Mokoena',
      email: 'sarah.mokoena@suninternational.com',
      phone: '+27 11 780 7000',
    },
    supportContact: {
      name: 'Group Guest Relations',
      email: 'guestrelations@suninternational.com',
      phone: '+27 11 780 7444',
    },
  },
  'aurora-collection': {
    organizationId: 'aurora-collection',
    legalName: 'Aurora Hotel Collection Ltd',
    tradingName: 'Aurora Hotel Collection',
    logoUrl: '',
    registrationNumber: '09482210',
    taxNumber: 'GB 284 5199 22',
    websiteUrl: 'https://www.aurorahotels.co.uk',
    address: {
      line1: '14 Buckingham Gate',
      line2: '',
      city: 'London',
      region: 'Greater London',
      postalCode: 'SW1E 6LB',
      country: 'United Kingdom',
    },
    primaryContact: {
      name: 'James Whitfield',
      email: 'james.whitfield@aurorahotels.co.uk',
      phone: '+44 20 7946 0912',
    },
    supportContact: {
      name: 'Aurora Guest Care',
      email: 'care@aurorahotels.co.uk',
      phone: '+44 20 7946 0900',
    },
  },
};

export const ORGANIZATION_DEFAULTS_SEED: Readonly<Record<string, OrganizationDefaults>> = {
  'sun-intl': {
    organizationId: 'sun-intl',
    lockedKeys: ['retention'],
  },
  'aurora-collection': {
    organizationId: 'aurora-collection',
    lockedKeys: [],
  },
};

export const ACCESS_POLICY_SEED: OrganizationAccessPolicy = {
  organizationId: PRIMARY_ORG_ID,
  ssoEnabled: true,
  ssoProviders: ['google', 'microsoft'],
  ssoEnforced: false,
  allowedEmailDomains: ['suninternational.com', 'sunint.co.za'],
  autoJoinEnabled: true,
  autoJoinRoleId: null,
  mfaMode: 'optional',
  mfaFactors: ['totp', 'email'],
  sessionIdleMinutes: 60,
  sessionAbsoluteHours: 12,
  passwordMinLength: 12,
  passwordRequireSymbols: true,
  ipAllowlist: [],
};

export const SENDING_DOMAINS_SEED: ReadonlyArray<SendingDomain> = [
  {
    id: 'dom-1',
    domain: 'mail.suninternational.com',
    status: 'verified',
    lastCheckedIso: '2026-08-29T06:12:00.000Z',
    records: [
      { kind: 'SPF', host: '@', value: 'v=spf1 include:mail.guestrevu.com ~all', verified: true },
      { kind: 'DKIM', host: 'gr._domainkey', value: 'v=DKIM1; k=rsa; p=MIGfMA0…', verified: true },
      {
        kind: 'DMARC',
        host: '_dmarc',
        value: 'v=DMARC1; p=quarantine; rua=mailto:dmarc@…',
        verified: true,
      },
    ],
  },
  {
    id: 'dom-2',
    domain: 'surveys.sunint.co.za',
    status: 'pending',
    lastCheckedIso: '2026-08-30T09:40:00.000Z',
    records: [
      { kind: 'SPF', host: '@', value: 'v=spf1 include:mail.guestrevu.com ~all', verified: true },
      { kind: 'DKIM', host: 'gr._domainkey', value: 'v=DKIM1; k=rsa; p=MIGfMA0…', verified: false },
      { kind: 'DMARC', host: '_dmarc', value: 'v=DMARC1; p=none', verified: false },
    ],
  },
];

export const EMAIL_SENDER_IDENTITY_SEED: EmailSenderIdentity = {
  organizationId: PRIMARY_ORG_ID,
  senderName: 'Sun International',
  fromAddress: 'stay@mail.suninternational.com',
  replyToAddress: 'guestrelations@suninternational.com',
  footerText:
    'Sun International Hotels Limited, 27 Sandton Drive, Sandhurst, Johannesburg 2196, South Africa.',
  unsubscribeText: 'Prefer not to hear from us? Unsubscribe here.',
};

export const RETENTION_POLICY_SEED: RetentionPolicy = {
  organizationId: PRIMARY_ORG_ID,
  reviewMonths: 36,
  guestMonths: 24,
  communicationMonths: 24,
  privacyPolicyUrl: 'https://www.suninternational.com/privacy',
  termsUrl: 'https://www.suninternational.com/terms',
  consentText:
    'We use your feedback to improve your next stay. You can ask us to delete your data at any time.',
  unsubscribeScope: 'organization',
};

export const API_KEYS_SEED: ReadonlyArray<ApiKey> = [
  {
    id: 'key-1',
    label: 'Data warehouse export',
    prefix: 'gr_live_7f2a',
    scopes: ['reviews:read', 'guests:read'],
    createdBy: 'Sarah Mokoena',
    createdAtIso: '2026-02-11T10:22:00.000Z',
    lastUsedIso: '2026-08-31T02:00:00.000Z',
    revoked: false,
  },
  {
    id: 'key-2',
    label: 'PMS guest push (Opera)',
    prefix: 'gr_live_c81d',
    scopes: ['guests:write'],
    createdBy: 'Devan Pillay',
    createdAtIso: '2025-11-02T08:15:00.000Z',
    lastUsedIso: '2026-08-30T23:45:00.000Z',
    revoked: false,
  },
  {
    id: 'key-3',
    label: 'Legacy reporting bridge',
    prefix: 'gr_live_44b0',
    scopes: ['reports:read'],
    createdBy: 'Sarah Mokoena',
    createdAtIso: '2024-06-18T13:05:00.000Z',
    lastUsedIso: null,
    revoked: true,
  },
];

export const WEBHOOKS_SEED: ReadonlyArray<WebhookSubscription> = [
  {
    id: 'hook-1',
    url: 'https://ops.suninternational.com/hooks/guestrevu',
    events: ['ticket.opened', 'ticket.escalated'],
    secretPrefix: 'whsec_9d21',
    isEnabled: true,
    recentDeliveries: [
      {
        id: 'd1',
        event: 'ticket.escalated',
        atIso: '2026-08-31T07:41:00.000Z',
        statusCode: 200,
        durationMs: 142,
      },
      {
        id: 'd2',
        event: 'ticket.opened',
        atIso: '2026-08-31T07:12:00.000Z',
        statusCode: 200,
        durationMs: 118,
      },
      {
        id: 'd3',
        event: 'ticket.opened',
        atIso: '2026-08-30T19:03:00.000Z',
        statusCode: 500,
        durationMs: 3011,
      },
    ],
  },
  {
    id: 'hook-2',
    url: 'https://hooks.slack.com/services/T00/B00/xxxx',
    events: ['review.created'],
    secretPrefix: 'whsec_1c07',
    isEnabled: false,
    recentDeliveries: [
      {
        id: 'd4',
        event: 'review.created',
        atIso: '2026-08-24T11:00:00.000Z',
        statusCode: 410,
        durationMs: 88,
      },
    ],
  },
];

export const DATA_IMPORT_SEED: DataImportConfig = {
  organizationId: PRIMARY_ORG_ID,
  connector: 'opera',
  isEnabled: true,
  scheduleLabel: 'Every day at 03:00 SAST',
  dedupeKey: 'email',
  mappings: [
    { sourceColumn: 'GUEST_EMAIL', targetField: 'email', required: true },
    { sourceColumn: 'GUEST_FIRST', targetField: 'firstName', required: true },
    { sourceColumn: 'GUEST_LAST', targetField: 'lastName', required: true },
    { sourceColumn: 'ARRIVAL_DT', targetField: 'arrival', required: true },
    { sourceColumn: 'DEPART_DT', targetField: 'departure', required: true },
    { sourceColumn: 'ROOM_NO', targetField: 'room', required: false },
    { sourceColumn: 'RATE_CODE', targetField: 'rateCode', required: false },
  ],
  runs: [
    {
      id: 'run-1',
      startedIso: '2026-08-31T01:00:00.000Z',
      rowsRead: 1842,
      rowsAccepted: 1836,
      rowsRejected: 6,
      status: 'partial',
    },
    {
      id: 'run-2',
      startedIso: '2026-08-30T01:00:00.000Z',
      rowsRead: 1719,
      rowsAccepted: 1719,
      rowsRejected: 0,
      status: 'succeeded',
    },
    {
      id: 'run-3',
      startedIso: '2026-08-29T01:00:00.000Z',
      rowsRead: 1903,
      rowsAccepted: 1903,
      rowsRejected: 0,
      status: 'succeeded',
    },
    {
      id: 'run-4',
      startedIso: '2026-08-28T01:00:00.000Z',
      rowsRead: 0,
      rowsAccepted: 0,
      rowsRejected: 0,
      status: 'failed',
    },
  ],
};

export const INVOICES_SEED: ReadonlyArray<Invoice> = [
  {
    id: 'inv-2026-08',
    number: 'GR-2026-0841',
    organizationId: PRIMARY_ORG_ID,
    periodStartIso: '2026-08-01',
    periodEndIso: '2026-08-31',
    issuedIso: '2026-08-01',
    amount: 12480,
    currency: 'USD',
    status: 'due',
    lines: [],
  },
  {
    id: 'inv-2026-07',
    number: 'GR-2026-0776',
    organizationId: PRIMARY_ORG_ID,
    periodStartIso: '2026-07-01',
    periodEndIso: '2026-07-31',
    issuedIso: '2026-07-01',
    amount: 12480,
    currency: 'USD',
    status: 'paid',
    lines: [],
  },
  {
    id: 'inv-2026-06',
    number: 'GR-2026-0702',
    organizationId: PRIMARY_ORG_ID,
    periodStartIso: '2026-06-01',
    periodEndIso: '2026-06-30',
    issuedIso: '2026-06-01',
    amount: 11940,
    currency: 'USD',
    status: 'paid',
    lines: [],
  },
  {
    id: 'inv-2026-05',
    number: 'GR-2026-0638',
    organizationId: PRIMARY_ORG_ID,
    periodStartIso: '2026-05-01',
    periodEndIso: '2026-05-31',
    issuedIso: '2026-05-01',
    amount: 11940,
    currency: 'USD',
    status: 'paid',
    lines: [],
  },
];
